import { useState } from 'react';
import {
  ROLE_LABEL,
  pulseAccessAnomalies,
  pulseActivation,
  pulseAnomalyReport,
  pulseCanary,
  pulseCanaryRun,
  pulseCerts,
  pulseConnectors,
  pulseModels,
  pulseProbes,
  pulseSecurityEvents,
  pulseSla,
  pulseUptime,
  pulseSloRows,
  pulseTestJob,
  pulseTestRun,
  type CanaryRunResult,
  type CanarySuite,
  type PulseModel,
  type PulseProjection,
  type PulseSuite,
  type PulseTestJob,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { sx } from '../../ui/designStyles';
import { HonestyBadge } from '../../ui/HonestyBadge';
import { SPEC, chipS, env, pre, usePulseApi, type SpecKey } from './lib';
import { BuildTagged, useCustomerText } from '../../ui/buildLabels';

const BLOCK_HEAD = 'display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:5px;padding-bottom:10px;border-bottom:1px solid rgba(34,39,46,.14);';
const H14 = 'font-size:16px;font-weight:700;letter-spacing:-.01em;';
const NOTE = 'font-size:14px;color:#54606C;margin-top:10px;line-height:1.5;max-width:100ch;text-wrap:pretty;';

// ── Connectors & probes (design puIsConn + puIsProbes, T8018-8030, T8430-8532) ──

/** The security signals' names as the design words them (V7 puSecEvents); the server sends ids. */
export const SECURITY_WORDS: Record<string, string> = {
  'inbound-integrity': 'Inbound integrity',
  'auth-failures': 'Auth failures',
  'authz-denials': 'Authz denials',
  'write-guard': 'Write-guard blocks',
  'ai-safety-guard': 'AI withheld',
  'kill-switch': 'Kill-switch trips',
};

export function ConnectorsTab({ canWrite, writeTitle }: { canWrite: boolean; writeTitle?: string }) {
  const { client } = useSession();
  const conn = usePulseApi((c, signal) => env(pulseConnectors(c, { signal })));
  const slo = usePulseApi((c, signal) => env(pulseSloRows(c, { signal })));
  const canary = usePulseApi((c, signal) => env(pulseCanary(c, { signal })));
  const anom = usePulseApi((c, signal) => env(pulseAnomalyReport(c, { signal })));
  const probes = usePulseApi((c, signal) => env(pulseProbes(c, { signal })));
  const sla = usePulseApi((c, signal) => env(pulseSla(c, { query: { scope: 'platform', windowDays: 30 }, signal })));
  // Restored from the Flutter Pulse (pulse_view.dart:160): each key signal's own uptime
  // over 7 days, from its recorded beats; "building history" until there are enough.
  const upPlatform = usePulseApi((c, signal) => env(pulseUptime(c, { query: { scope: 'platform', scopeId: '', signal: 'rollup', windowDays: 7 }, signal })));
  const upErrors = usePulseApi((c, signal) => env(pulseUptime(c, { query: { scope: 'service', scopeId: 'requests', signal: 'error-rate', windowDays: 7 }, signal })));
  const upLatency = usePulseApi((c, signal) => env(pulseUptime(c, { query: { scope: 'service', scopeId: 'requests', signal: 'latency-p95', windowDays: 7 }, signal })));
  const perSignal = ([['Platform', upPlatform.data], ['Error rate', upErrors.data], ['Latency p95', upLatency.data]] as const)
    .map(([k, u]) => k + ' ' + (u && !u.buildingHistory && u.uptimePct != null ? u.uptimePct.toFixed(2) + '%' : 'building history'))
    .join(' · ');
  const [err, setErr] = useState<string | null>(null);
  const WINDOW_DAYS = 30;

  const connectors = conn.data?.connectors ?? [];
  const fromLast = (key: string, name: string, r: CanaryRunResult | null | undefined): CanarySuite | null =>
    r ? { key, name, path: r.steps.map((s) => s.name).join(' → '), status: r.status, durationMs: r.durationMs, ranAt: r.ranAt, reason: null, streak: r.streak, history: '' } : null;
  const suites: CanarySuite[] =
    canary.data?.suites ??
    [fromLast('platform', 'platform · app golden path', canary.data?.last), fromLast('care', 'care · patient-safety path', canary.data?.care.last)].filter((x): x is CanarySuite => x !== null);
  // When this person pressed Run (design puCanaryRun): suites whose last run is at or after
  // it read "just now · you ran it", and the button counts what ran from the refreshed list.
  const [ranAt, setRanAt] = useState<number | null>(null);
  const runAll = () => {
    if (!canWrite) return;
    setErr(null);
    const t0 = Date.now();
    Promise.all([pulseCanaryRun(client), pulseCanaryRun(client, { query: { suite: 'care' } })]).then(
      () => {
        setRanAt(t0);
        canary.reload();
      },
      (e: unknown) => setErr(e instanceof Error ? e.message : 'The canaries could not run.'),
    );
  };
  const mine = (s: CanarySuite) => ranAt !== null && !!s.ranAt && Date.parse(s.ranAt) >= ranAt - 1000;
  const ran = (() => {
    if (ranAt === null) return null;
    const did = suites.filter(mine);
    const passed = did.filter((x) => x.status === 'pass').length;
    const skipped = suites.filter((x) => x.status === 'skip').length;
    const na = suites.filter((x) => x.status === 'n/a').length;
    return 'Ran ' + did.length + ' suites · ' + passed + ' passed' + (skipped || na ? ' · ' + [skipped ? skipped + ' skipped' : '', na ? na + ' n/a' : ''].filter(Boolean).join(', ') : '');
  })();
  const now = Date.now();
  const whenOf = (s: CanarySuite) => (s.ranAt ? Math.max(0, Math.round((now - Date.parse(s.ranAt)) / 1000)) + 's ago' : s.reason ?? 'never run');
  const secsAgo = (s: CanarySuite) => {
    if (!s.ranAt) return whenOf(s);
    const sec = Math.max(0, Math.round((now - Date.parse(s.ranAt)) / 1000));
    return sec < 60 ? sec + 's ago' : Math.floor(sec / 60) + 'm ago';
  };

  return (
    <>
      <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:15px 17px;')} data-testid="pulse-connectors">
        {connectors.length === 0 ? (
          <>
            <div style={sx('font-size:16px;font-weight:700;')}>{conn.error ? 'Connector health could not be read' : 'No connector configured'}</div>
            <div style={sx('font-size:14px;color:#54606C;margin-top:5px;line-height:1.55;max-width:90ch;text-wrap:pretty;')}>
              <span>A connector is the pipe an EHR pushes through. None is configured, so there is no last-received time, no HMAC-reject count and no unmatched-record count - all three would be fabrications.</span>
            </div>
            <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin:14px 0 8px;')}>What it will watch</div>
            <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:0 18px;align-items:start;min-width:0;')}>
              {[
                ['last received', 'stale over 6h, down over 24h - the silently-dead-pipe alarm'],
                ['HMAC rejects', 'rejects above zero with zero receives reads as down, not quiet'],
                ['unmatched records', 'durable ledger events, so counts survive a redeploy'],
              ].map((c) => (
                <div key={c[0]} style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:4px;padding:8px 0;border-top:1px solid rgba(34,39,46,.07);')}>
                  <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;white-space:nowrap;')}>
                    <span>{c[0]}</span>
                  </span>
                  <span style={sx('flex:1 1 130px;min-width:min(100%,130px);font-size:14px;color:#54606C;line-height:1.45;text-wrap:pretty;')}>
                    <span>{c[1]}</span>
                  </span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <div style={sx('font-size:14px;font-weight:700;')}>{connectors.length + (connectors.length === 1 ? ' connector' : ' connectors')}</div>
            {connectors.map((c) => {
              const k: SpecKey = c.status === 'stale' ? 'degraded' : c.status;
              return (
                <div key={c.connector} style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:4px;padding:8px 0;border-top:1px solid rgba(34,39,46,.07);')}>
                  <span style={sx('font-size:14px;font-weight:700;white-space:nowrap;')}>
                    <span>{c.connector}</span>
                  </span>
                  <span style={sx(chipS(SPEC[k][0], SPEC[k][1]))}>
                    <span>{c.status.toUpperCase()}</span>
                  </span>
                  <span style={sx('flex:1 1 130px;min-width:min(100%,130px);font-size:14px;color:#54606C;line-height:1.45;text-wrap:pretty;')}>
                    <span>{c.detail + ' · ' + c.hmacRejects + ' HMAC rejects · ' + c.unmatched + ' unmatched of ' + c.received}</span>
                  </span>
                </div>
              );
            })}
          </>
        )}
      </div>

      <div style={sx('display:flex;flex-direction:column;gap:12px;')} data-testid="pulse-probes">
        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:14px 16px;')}>
          <div style={sx(BLOCK_HEAD)}>
            <span style={sx(H14)}>Error budget &amp; burn rate</span>
          </div>
          <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:0 18px;align-items:start;min-width:0;')}>
            {(slo.data?.slos ?? []).map((s) => {
              const has = s.targetPct !== null;
              const spent = has && s.budgetRemainingPct !== null ? Math.round(100 - s.budgetRemainingPct) : null;
              // The longest window that has a burn rate: a fast burn has only its 1h and 6h yet.
              const burn = s.burn24h ?? s.burn6h ?? s.burn1h;
              const burnWin = s.burn24h != null ? '' : s.burn6h != null ? ' 6h' : s.burn1h != null ? ' 1h' : '';
              const hot = has && burn !== null && burn > 1;
              const left = spent === null ? null : 100 - spent;
              const color = spent === null ? '#54606C' : spent > 90 ? '#B3261E' : spent > 60 ? '#5B6B7C' : '#1F6B52';
              const eta = !has ? 'no budget to burn' : spent === null || burn === null ? (burn !== null && burn > 1 ? 'burning ' + burn.toFixed(1) + 'x; budget spent not measured yet' : 'budget not measured yet') : (left as number) <= 0 ? 'budget already spent over the recorded window' : burn <= 1 ? 'holds to the end of the recorded window at this rate' : (() => {
                const hrs = (left as number) / (burn * (100 / (s.windowDays * 24)));
                return 'exhausted in ' + (hrs < 1 ? '~' + Math.max(1, Math.round(hrs * 60)) + 'm' : Math.round(hrs * 10) / 10 + 'h') + ' at this rate';
              })();
              return (
                <div key={s.signal} style={sx('display:grid;grid-template-columns:minmax(0,1fr) 96px 62px;gap:7px 13px;align-items:baseline;padding:11px 0;border-bottom:1px solid rgba(34,39,46,.07);')}>
                  <span style={sx('min-width:0;')}>
                    <span style={sx('display:block;font-size:16px;font-weight:600;')}>
                      <span>{s.name ?? s.signal}</span>
                    </span>
                    <span style={sx('display:block;font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;margin-top:3px;')}>
                      <span>{has ? (s.objective ?? s.targetPct + '%') + ' · ' + s.windowDays + 'd objective · measured over the recorded window' : 'no objective set'}</span>
                    </span>
                    <span style={sx('display:block;font-size:14px;color:#54606C;margin-top:3px;line-height:1.45;text-wrap:pretty;')}>
                      <span>{s.measures ?? s.detail}</span>
                    </span>
                  </span>
                  <span style={sx('font-variant-numeric:tabular-nums;font-size:16px;font-weight:700;text-align:right;white-space:nowrap;color:' + color + ';')}>
                    <span>{spent !== null ? spent + '% spent' : '–'}</span>
                    <span style={sx('display:block;font-variant-numeric:tabular-nums;font-size:14px;font-weight:400;color:#54606C;margin-top:2px;')}>
                      <span>{spent !== null ? 'depth not exposed' : ''}</span>
                    </span>
                  </span>
                  <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;text-align:right;white-space:nowrap;color:' + (!has ? '#54606C' : hot ? '#B3261E' : '#54606C') + ';')}>
                    <span>{has && burn !== null ? burn.toFixed(1) + 'x' + burnWin : '–'}</span>
                  </span>
                  <span style={sx('grid-column:1 / -1;display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:5px;')}>
                    {has && (
                      <span style={sx('flex:1 1 160px;min-width:min(100%,160px);height:6px;border-radius:3px;background:rgba(34,39,46,.09);overflow:hidden;')}>
                        <span style={sx('display:block;height:100%;border-radius:3px;width:' + (spent === null ? 0 : Math.min(100, spent)) + '%;background:' + (spent === null ? 'transparent' : color) + ';')} />
                      </span>
                    )}
                    <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>
                      <span>{eta}</span>
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:14px 16px;')}>
          <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:7px;padding-bottom:10px;border-bottom:1px solid rgba(34,39,46,.14);')}>
            <span style={sx(H14)}>Canaries</span>
            <span style={sx('flex:1 1 40px;min-width:20px;')} />
            <a
              href="#"
              aria-disabled={!canWrite ? 'true' : undefined}
              title={!canWrite ? writeTitle : undefined}
              onClick={(e) => {
                pre(e);
                runAll();
              }}
              style={sx('display:inline-flex;align-items:center;height:30px;padding:0 13px;border-radius:8px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;' + (ran ? 'background:rgba(47,138,111,.14);color:#1F6B52;border:1px solid rgba(47,138,111,.34);' : 'background:#2F5C97;color:#fff;border:1px solid #2F5C97;'))}
            >
              <span>{ran ?? 'Run all canaries now'}</span>
            </a>
          </div>
          {err && <div role="alert" style={sx('font-size:14px;color:#B3261E;font-weight:700;margin-top:6px;')}>{err}</div>}
          {suites.map((c) => (
            <div key={c.key} style={sx('display:grid;grid-template-columns:minmax(0,1fr) 132px 54px;gap:6px 13px;align-items:baseline;padding:10px 0;border-bottom:1px solid rgba(34,39,46,.07);')}>
              <span style={sx('min-width:0;')}>
                <span style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:5px;')}>
                  <span style={sx('font-size:16px;font-weight:600;')}>
                    <span>{c.name}</span>
                  </span>
                  <span style={sx(chipS(c.status === 'pass' ? '#2F5C97' : c.status === 'fail' ? '#B3261E' : '#54606C', c.status === 'pass' ? 'rgba(79,138,214,.13)' : c.status === 'fail' ? 'rgba(179,38,30,.10)' : 'rgba(34,39,46,.07)'))}>
                    <span>{c.status.toUpperCase()}</span>
                  </span>
                </span>
                <span style={sx('display:block;font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;margin-top:3px;')}>
                  <span>{c.path}</span>
                </span>
              </span>
              <span data-testid="canary-streak" style={sx('font-variant-numeric:tabular-nums;font-size:14px;white-space:nowrap;color:' + streakTone(c.status, c.streak) + ';')}>
                <span>{c.streak ? c.streak.toLocaleString('en-US') + (c.status === 'fail' ? ' fails running' : ' passes running') : 'never run'}</span>
              </span>
              <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;text-align:right;white-space:nowrap;')}>
                <span>{c.durationMs != null ? (c.durationMs / 1000).toFixed(1) + 's' : '–'}</span>
              </span>
              <span style={sx('grid-column:1 / -1;display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:4px;')}>
                <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;white-space:nowrap;')}>
                  <span>{mine(c) && c.status === 'pass' ? 'just now · you ran it' : secsAgo(c)}</span>
                </span>
                <span style={sx('font-size:14px;color:#54606C;line-height:1.45;text-wrap:pretty;')}>
                  <span>{c.history}</span>
                </span>
              </span>
            </div>
          ))}
          <div style={sx(NOTE)}>
            <span>Suites are platform (the app golden path), care (the patient-safety path) and login (the real user path). Every canary runs on a 5-minute schedule - 288 runs a day, which is what the pass counts are counted in. A canary walks the whole journey end to end, so it fails when the parts are individually healthy but the path between them is not. Read-only: it writes to a synthetic resident, never a real one.</span>
          </div>
        </div>

        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:14px 16px;')}>
          <div style={sx('font-size:16px;font-weight:700;letter-spacing:-.01em;padding-bottom:10px;border-bottom:1px solid rgba(34,39,46,.14);')}>Anomalies vs each signal{'’'}s own baseline</div>
          {(anom.data?.signals ?? []).map((a) => {
            const c: [string, string] = a.state === 'flagged' ? ['#B3261E', 'rgba(179,38,30,.12)'] : a.state === 'building' ? ['#54606C', 'rgba(34,39,46,.07)'] : ['#1F6B52', 'rgba(47,138,111,.14)'];
            return (
              <div key={a.name} style={sx('display:flex;align-items:baseline;gap:11px;flex-wrap:wrap;row-gap:5px;padding:10px 0;border-bottom:1px solid rgba(34,39,46,.07);')}>
                <span style={sx('font-variant-numeric:tabular-nums;font-size:16px;font-weight:700;white-space:nowrap;')}>
                  <span>{a.name}</span>
                </span>
                <span style={sx(chipS(c[0], c[1]))} title={a.note}>
                  <span>{a.state}</span>
                </span>
              </div>
            );
          })}
          <div style={sx(NOTE)}>
            <span>Every flag is against that signal{'’'}s own baseline, like-for-like - a completed day against completed days, so a partial day never pages anyone.</span>
          </div>
          <div style={sx('margin-top:14px;padding-top:12px;border-top:1px solid rgba(34,39,46,.12);')}>
            <div style={sx('font-size:16px;font-weight:700;margin-bottom:8px;')}>Per-facility request health</div>
            <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:0 18px;align-items:start;min-width:0;')}>
              {[
                ...(anom.data?.baselines ?? []).map((b) => ({
                  key: b.id,
                  name: anom.data?.baselineNames?.[b.id] ?? b.id,
                  detail: b.state === 'flagged' ? b.errorPct + '% error rate vs a ' + b.errorBaselinePct + '% own baseline · p95 ' + (b.p95Ms / 1000).toFixed(1) + 's vs ' + (b.p95BaselineMs / 1000).toFixed(1) + 's' : 'error rate and p95 inside baseline',
                  state: b.state,
                  reqs: b.receives,
                })),
                ...(anom.data?.buildingBaseline ?? []).map((b) => ({ key: b.id + b.signal, name: anom.data?.baselineNames?.[b.id] ?? b.label ?? b.id, detail: b.detail, state: 'not judged', reqs: b.have })),
              ].map((a) => (
                <div key={a.key} style={sx('display:grid;grid-template-columns:minmax(0,1fr) minmax(0,120px) minmax(0,74px);gap:6px 12px;align-items:baseline;padding:9px 0;border-bottom:1px solid rgba(34,39,46,.07);')}>
                  <span style={sx('min-width:0;')}>
                    <span style={sx('display:block;font-size:14px;font-weight:600;')}>
                      <span>{a.name}</span>
                    </span>
                    <span style={sx('display:block;font-size:14px;color:#54606C;line-height:1.45;margin-top:3px;text-wrap:pretty;')}>
                      <span>{a.detail}</span>
                    </span>
                  </span>
                  <span style={sx(chipS(a.state === 'flagged' ? '#B3261E' : a.state === 'normal' ? '#2F5C97' : '#54606C', a.state === 'flagged' ? 'rgba(179,38,30,.10)' : a.state === 'normal' ? 'rgba(79,138,214,.13)' : 'rgba(34,39,46,.07)'))}>
                    <span>{a.state.toUpperCase()}</span>
                  </span>
                  <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;text-align:right;white-space:nowrap;')}>
                    <span>{a.reqs + ' req'}</span>
                  </span>
                </div>
              ))}
            </div>
            <div style={sx('font-size:14px;color:#54606C;margin-top:9px;line-height:1.5;max-width:100ch;text-wrap:pretty;')}>
              <span>Judged against each facility{'’'}s own baseline, not the platform average. Anything under 20 requests in the window is left unjudged rather than flagged on a thin sample, and a facility flag is de-duped against the platform-wide anomaly so one outage is not two alerts.</span>
            </div>
          </div>
        </div>

        <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:11px;')}>
          {[
            ['Platform · ' + WINDOW_DAYS + 'd', sla.data && !sla.data.buildingHistory ? sla.data.uptimePct.toFixed(2) + '%' : 'not computed', sla.data && !sla.data.buildingHistory ? '#22272E' : '#54606C'],
            ['MTTR', sla.data?.mttrMinutes != null && !sla.data.buildingHistory ? sla.data.mttrMinutes + 'm' : 'not computed', sla.data?.mttrMinutes != null && !sla.data.buildingHistory ? '#22272E' : '#54606C'],
            ['Beats recorded', 'not exposed', '#54606C'],
            ['Retention', WINDOW_DAYS + ' days', '#22272E'],
          ].map((u) => (
            <div key={u[0]} style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:12px 14px;')}>
              <div style={sx('font-size:14px;letter-spacing:.1em;text-transform:uppercase;color:#54606C;font-weight:600;')}>
                <span>{u[0]}</span>
              </div>
              <div style={sx('font-size:20px;font-weight:700;letter-spacing:-.02em;margin-top:2px;color:' + u[2] + ';')}>
                <span>{u[1]}</span>
              </div>
            </div>
          ))}
        </div>

        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:9px 2px;')}>
          <div style={sx('display:flex;align-items:baseline;gap:4px 12px;flex-wrap:wrap;margin-bottom:9px;')}>
            <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Deep infra probes</span>
            {/* Per-signal uptime (a production line the design has no row for) rides in the
                header so the V4 layout keeps its height. */}
            <span data-testid="pulse-signal-uptime" style={sx('font-size:14px;color:#54606C;line-height:1.5;font-variant-numeric:tabular-nums;min-width:0;')}>
              <span>{'Per signal · 7d: ' + perSignal}</span>
            </span>
            <span style={sx('flex:1 1 10px;')} />
            <SuiteRunBar canWrite={canWrite} writeTitle={writeTitle} />
          </div>
          <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:0 18px;align-items:start;min-width:0;')}>
            {(probes.data?.checks ?? []).map((p) => (
              <div key={p.name} style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:5px;padding:9px 0;border-top:1px solid rgba(34,39,46,.07);')}>
                <span style={sx('flex:1 1 130px;min-width:min(100%,130px);font-size:16px;')}>
                  <span>{p.data?.label ?? p.name}</span>
                </span>
                <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#3C454F;white-space:nowrap;')}>
                  <span>{p.detail}</span>
                </span>
                <span style={sx(chipS(SPEC[p.status][0], SPEC[p.status][1]))}>
                  <span>{p.status.toUpperCase()}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Run the smoke or deep probe suite by hand (POST /api/pulse/test/run, then poll the job).
 * Restored from the Flutter console. The V3 design's run bar (puForce, T7781-7790) sits on
 * its retired Heartbeat tab, so its two actions and its result line are drawn here as text
 * links on the probes' own heading line: the tab keeps the design's height.
 */
export function SuiteRunBar({ canWrite, writeTitle }: { canWrite: boolean; writeTitle?: string }) {
  const { client } = useSession();
  const [job, setJob] = useState<PulseTestJob | null>(null);
  const [running, setRunning] = useState<PulseSuite | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const run = async (suite: PulseSuite) => {
    if (!canWrite || running) return;
    setErr(null);
    setRunning(suite);
    try {
      const started = await pulseTestRun(client, { body: { suite } });
      let j: PulseTestJob | null = null;
      for (let n = 0; n < 60; n++) {
        j = (await pulseTestJob(client, { params: { jobId: started.jobId } })).data;
        if (j.status !== 'running') break;
        await new Promise((r) => setTimeout(r, 500));
      }
      setJob(j);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'The run could not start.');
    } finally {
      setRunning(null);
    }
  };
  const FORCE: [PulseSuite, string, string][] = [
    ['smoke', 'Run smoke tests', '6 read-only probes · no writes, no token burn'],
    ['deep', 'Deep run', '7 probes + replication lag, cron duration, queue depth'],
  ];
  const pass = job && job.status === 'completed' && job.failed === 0;
  const label = running
    ? running + ' run · running'
    : job
      ? job.suite + ' run · ' + (pass ? 'PASS' : 'FAIL') + ' · ' + job.passed + '/' + job.total + ' · ' + (job.durationMs / 1000).toFixed(1) + 's'
      : '';
  return (
    <span data-testid="pulse-suite-run" style={sx('display:inline-flex;align-items:baseline;gap:4px 12px;flex-wrap:wrap;min-width:0;')}>
      {label && <span style={sx('font-size:14px;font-weight:700;letter-spacing:.06em;color:' + (pass || running ? '#1F6B52' : '#B3261E') + ';')}>{label}</span>}
      {err && (
        <span role="alert" style={sx('font-size:14px;color:#B3261E;font-weight:700;')}>
          {err}
        </span>
      )}
      {FORCE.map(([suite, name, note]) => (
        <a
          key={suite}
          href="#"
          title={canWrite ? note + ' · read-only probes, no writes' : writeTitle}
          aria-disabled={!canWrite || !!running ? 'true' : undefined}
          onClick={(e) => {
            pre(e);
            void run(suite);
          }}
          style={sx('font-size:14px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;')}
        >
          {name}
        </a>
      ))}
    </span>
  );
}

// ── Models (design puIsModels, T7923-7955; pfVals MODELS) ──

export function modelRow(m: PulseModel) {
  const acting = m.mode === 'acting';
  const rule = m.kind === 'rule';
  const live = Array.isArray(m.live_in) ? (m.live_in.length && typeof m.live_in[0] === 'string' ? (m.live_in as string[]).join(', ') : m.live_in.length + ' live buildings') : 'not tracked';
  const cal = rule ? 'n/a (rules)' : m.calibration ? m.calibration.metric + ' ' + (m.calibration.value ?? '') : null;
  const fpr = m.fpr_applicable === false ? 'n/a' : m.fpr != null ? (m.fpr * 100).toFixed(1) + '%' : null;
  const sub = m.subgroup_applicable === false || rule ? 'n/a' : m.subgroup.length ? m.subgroup.length + ' subgroups' : null;
  const shadow = rule ? 'n/a (rules)' : acting ? 'n/a (acting)' : m.shadow_note ?? (m.shadow_result ? 'shadow results on record' : 'no shadow run');
  const flag =
    m.flag_note ??
    (m.flags.includes('acting_without_calibration') ? 'Acting with no calibration record or false positive rate. The spec says a facility must never act on a score without all three.' : m.flags.includes('drift_breached') ? 'Drift breached its threshold on the last run.' : '');
  const nc = (v: string | null) => ({ t: v === null ? 'Not computed' : v, s: 'font-size:14px;line-height:1.4;text-wrap:pretty;' + (v === null ? (acting ? 'color:#B3261E;font-weight:700;' : 'color:#54606C;') : 'color:#3C454F;') });
  return {
    acting,
    name: m.name,
    what: m.description ?? '',
    version: m.version ?? '–',
    mode: m.mode === 'acting' ? 'Acting' : m.mode === 'shadow' ? 'Shadow' : 'Off',
    liveIn: live,
    cal: nc(cal),
    fpr: nc(fpr),
    sub: nc(sub),
    shadow,
    flag,
    missing: acting && ((cal === null && !rule) || fpr === null),
  };
}

export function ModelsTab() {
  const q = usePulseApi((c, signal) => pulseModels(c, { signal }));
  const rows = (q.data?.models ?? []).filter((m) => m.mode !== 'off').map(modelRow);
  const flagged = rows.filter((r) => r.flag).length;
  const missing = rows.filter((r) => r.missing).length;
  // No acting model is not an all-clear: with none registered the line is neutral, never green.
  const none = !!q.data && rows.length === 0;
  const verdict = q.error
    ? 'The model registry could not be read'
    : !q.data
      ? 'Loading the model registry'
      : none
        ? 'No acting models registered yet'
        : flagged
          ? flagged + ' of ' + rows.length + ' models need attention · ' + missing + ' acting without calibration or false positive rate on record'
          : 'Every acting model has version, calibration and false positive rate on record';
  const COLS = 'display:grid;grid-template-columns:minmax(0,1.6fr) minmax(0,.8fr) minmax(0,.7fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1.3fr);gap:10px;';
  return (
    <div data-screen-label="Pulse · Models" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:13px 15px;display:flex;flex-direction:column;gap:10px;min-width:0;')} data-testid="pulse-models">
      <div style={sx('display:flex;align-items:baseline;gap:6px 10px;flex-wrap:wrap;min-width:0;')}>
        <span data-testid="models-verdict" style={sx('font-size:16px;font-weight:700;letter-spacing:-.01em;line-height:1.3;text-wrap:pretty;color:' + (flagged || q.error ? '#B3261E' : none || !q.data ? '#54606C' : '#1F6B52') + ';')}>
          <span>{verdict}</span>
        </span>
        <span style={sx('flex:1 1 20px;')} />
      </div>
      <div style={sx('font-size:14px;color:#3C454F;line-height:1.55;max-width:96ch;text-wrap:pretty;')}>
        <span>Pulse asks whether everything is working. This tab asks whether it is still right: each model, the version live in each building, whether its scores still match what happens, how often it is wrong, and whether it is worse for any group of residents. Internal only.</span>
      </div>
      <div style={sx('overflow-x:auto;min-width:0;')}>
        <div style={sx('min-width:900px;')}>
          <div style={sx(COLS + 'padding:6px 0;font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:700;font-size:14px;')}>
            <span>Model</span>
            <span>Version</span>
            <span>Mode</span>
            <span>Live in</span>
            <span>Calibration</span>
            <span>False positives</span>
            <span>Subgroups</span>
            <span>Shadow check</span>
          </div>
          {rows.map((m) => (
            <div key={m.name} style={sx('border-top:1px solid rgba(34,39,46,.1);padding:9px 0;')} data-testid="pulse-model">
              <div style={sx(COLS + 'align-items:baseline;font-size:14px;line-height:1.4;')}>
                <span style={sx('min-width:0;')}>
                  <span style={sx('display:block;font-weight:700;font-size:16px;')}>
                    <span>{m.name}</span>
                  </span>
                  <span style={sx('display:block;color:#54606C;font-size:14px;text-wrap:pretty;')}>
                    <span>{m.what}</span>
                  </span>
                </span>
                <span style={sx("font-variant-numeric:tabular-nums;font-size:14px;")}>
                  <span>{m.version}</span>
                </span>
                <span>
                  <span style={sx('display:inline-flex;align-items:center;height:22px;padding:0 7px;border-radius:4px;font-size:14px;font-weight:800;letter-spacing:.06em;' + (m.acting ? 'color:#264C7E;background:rgba(47,92,151,.12);' : 'color:#54606C;background:rgba(34,39,46,.08);'))}>
                    <span>{m.mode}</span>
                  </span>
                </span>
                <span style={sx('color:#3C454F;')}>
                  <span>{m.liveIn}</span>
                </span>
                <span style={sx(m.cal.s)}>
                  <span>{m.cal.t}</span>
                </span>
                <span style={sx(m.fpr.s)}>
                  <span>{m.fpr.t}</span>
                </span>
                <span style={sx(m.sub.s)}>
                  <span>{m.sub.t}</span>
                </span>
                <span style={sx('color:#3C454F;text-wrap:pretty;')}>
                  <span>{m.shadow}</span>
                </span>
              </div>
              {m.flag && (
                <div style={sx('font-size:14px;font-weight:700;color:#B3261E;line-height:1.45;margin-top:5px;text-wrap:pretty;')}>
                  <span>{m.flag}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div style={sx('font-size:14px;color:#54606C;line-height:1.5;max-width:100ch;text-wrap:pretty;')}>
        <span>Not computed means the number does not exist yet, not that it is fine. Staff overriding a gate is recorded as a by-product of their work; once summarized per model it becomes the clinical-feedback signal the guidance accepts.</span>
      </div>
    </div>
  );
}

// ── Security (design puIsSecurity + puIsAccess2, T8335-8428) ──

export function SecurityTab() {
  const certs = usePulseApi((c, signal) => env(pulseCerts(c, { signal })));
  const events = usePulseApi((c, signal) => env(pulseSecurityEvents(c, { signal })));
  const ueba = usePulseApi((c, signal) => env(pulseAccessAnomalies(c, { signal })));
  const arm = usePulseApi((c, signal) => env(pulseActivation(c, { signal })));
  const armed = certs.data?.armed === true;
  const sevOf = (s: string) => (s === 'high' ? ['#B3261E', 'rgba(179,38,30,.10)'] : s === 'medium' ? ['#5B6B7C', 'rgba(91,107,124,.11)'] : ['#54606C', 'rgba(34,39,46,.06)']);
  const cTone = (d: number | null): [string, string] => (!armed || d === null ? ['#54606C', 'transparent'] : d <= 7 ? ['#B3261E', 'rgba(179,38,30,.10)'] : d <= 21 ? ['#5B6B7C', 'rgba(91,107,124,.08)'] : ['#2F5C97', 'transparent']);
  const HEAD = 'font-size:14px;letter-spacing:.09em;text-transform:uppercase;white-space:nowrap;color:#54606C;font-weight:700;';
  const certFlags = certs.data ? [{ real: certs.data.real, basis: certs.data.basis }] : [];
  const uebaFlags = ueba.data ? [{ real: ueba.data.real, basis: ueba.data.basis }] : [];
  // The * note only when a row carries one (a day count shown with the probe off).
  const starred = !armed && (certs.data?.certs ?? []).some((c) => c.daysRemaining !== null);
  const secNote =
    (starred
      ? 'Day counts marked * are the shape of a reading, not one: PULSE_CERT_PROBE is off, so no row can claim a state. '
      : !armed
        ? 'PULSE_CERT_PROBE is off, so no certificate row can claim a state. '
        : '') +
    'PHI-read detectors do run and surface below, but PULSE_PHI_ACCESS_PAGE is off, so nothing here wakes anyone yet. ' +
    (armed ? 'It is listed under Not armed yet with what turning it on would change.' : 'Both are listed under Not armed yet with what turning them on would change.');
  return (
    <>
      <div style={sx('display:flex;flex-direction:column;gap:11px;min-width:0;')} data-testid="pulse-security">
        <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;')}>
          <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;letter-spacing:.15em;text-transform:uppercase;color:#54606C;font-weight:700;white-space:nowrap;')}>Data security</span>
          <HonestyBadge flags={certFlags} figure="Certificate expiry" className="ps-badge ps-badge--red" />
          <span style={sx('flex:1 1 60px;min-width:40px;height:1px;background:rgba(34,39,46,.1);')} />
        </div>
        <div style={sx('font-size:16px;line-height:1.55;max-width:88ch;text-wrap:pretty;color:#54606C;')}>
          <span>{armed ? 'Certificate expiry and security-event rates. The certificate probe is armed, so every day count is a reading. PHI-read detectors have their own tab.' : 'Certificate expiry and security-event rates. The certificate probe is not armed, so its day counts are the shape rather than a reading and no row carries a countdown. PHI-read detectors have their own tab.'}</span>
        </div>
        <div style={sx('margin-top:11px;')}>
          <div style={sx('display:grid;grid-template-columns:minmax(140px,1fr) 52px minmax(0,96px) minmax(0,1fr) minmax(0,1fr);gap:2px 12px;align-items:center;padding:0 12px 6px;border-bottom:1px solid rgba(34,39,46,.16);')}>
            <span style={sx(HEAD)}>Certificate</span>
            <span style={sx(HEAD)}>Left</span>
            <span style={sx(HEAD)}>State</span>
            <span style={sx(HEAD)}>Issuer</span>
            <span style={sx(HEAD)}>Renewal</span>
          </div>
          {/* One row per certificate, under the header's columns. */}
          <div data-testid="pulse-certs" style={sx('display:flex;flex-direction:column;min-width:0;')}>
            {(certs.data?.certs ?? []).map((c) => {
              const d = c.daysRemaining;
              const t = cTone(d);
              const fore = !armed || d === null ? '' : d <= 7 ? 'inside the down line · expires in ' + d + (d === 1 ? ' day' : ' days') : d <= 21 ? 'degraded now · ' + (d - 7) + ' days to the 7-day down line' : 'crosses the 21-day degraded line in ' + (d - 21) + ' days';
              return (
                <div key={c.host} style={sx('display:grid;grid-template-columns:minmax(140px,1fr) 52px minmax(0,96px) minmax(0,1fr) minmax(0,1fr);gap:2px 12px;align-items:center;padding:9px 12px;border-bottom:1px solid rgba(34,39,46,.07);background:' + t[1] + ';')}>
                  <span style={sx('font-variant-numeric:tabular-nums;font-size:16px;font-weight:700;min-width:0;overflow-wrap:anywhere;')}>
                    <span>{c.host}</span>
                  </span>
                  <span style={sx('font-variant-numeric:tabular-nums;font-size:16px;font-weight:700;color:' + (armed ? t[0] : '#54606C') + ';')}>
                    <span>{d === null ? '–' : armed ? d + 'd' : d + 'd*'}</span>
                  </span>
                  <span style={sx(chipS('#54606C', 'rgba(34,39,46,.07)'))}>
                    <span>{armed ? c.status.toUpperCase() : 'PROBE OFF'}</span>
                  </span>
                  <span style={sx('font-size:14px;color:#54606C;')}>
                    <span>{c.issuer ?? '–'}</span>
                  </span>
                  <span style={sx('font-size:14px;color:#54606C;')}>
                    <span>{c.renewal ?? '–'}</span>
                  </span>
                  <span style={sx((armed ? '' : 'display:none;') + 'font-size:14px;font-weight:700;line-height:1.4;text-wrap:pretty;color:' + (d !== null && d <= 7 ? '#B3261E' : d !== null && d <= 21 ? '#5B6B7C' : '#54606C') + ';grid-column:1/-1;margin-top:3px;')}>
                    <span>{fore}</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(230px,1fr));gap:11px;margin-top:12px;')}>
          <div style={sx('min-width:0;')}>
            <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(290px,1fr));gap:0 16px;align-items:start;min-width:0;')}>
              {(events.data?.signals ?? []).map((e) => (
                <div key={e.name} style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:11px 13px;')}>
                  <div style={sx('font-size:14px;color:#54606C;')}>
                    <span>{e.label ?? SECURITY_WORDS[e.name] ?? e.name}</span>
                  </div>
                  <div style={sx('font-variant-numeric:tabular-nums;font-size:16px;font-weight:700;color:' + (e.status === 'unknown' ? '#54606C' : '#22272E') + ';')}>
                    <span>{e.value ?? String(e.count)}</span>
                  </div>
                  <div style={sx('font-size:14px;color:#54606C;line-height:1.4;margin-top:3px;text-wrap:pretty;')}>
                    <span>{e.detail}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div style={sx('font-size:14px;color:#54606C;margin-top:9px;line-height:1.5;max-width:96ch;text-wrap:pretty;')}>
          <span data-testid="pulse-security-note">{secNote}</span>
        </div>
      </div>

      <div style={sx('display:flex;flex-direction:column;gap:11px;min-width:0;')} data-testid="pulse-access">
        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:14px 16px;margin-top:12px;')}>
          <div style={sx(BLOCK_HEAD)}>
            <span style={sx(H14)}>PHI-read access anomalies</span>
            <HonestyBadge flags={uebaFlags} figure="PHI-read access anomalies" className="ps-badge" />
            <span style={sx(chipS('#54606C', 'rgba(34,39,46,.07)'))}>
              <span>{ueba.data ? (ueba.data.building ? 'LEARNING BASELINE' : 'BASELINE SET') + ' · ' + ueba.data.usersEvaluated + ' users seen' : 'NOT READ'}</span>
            </span>
          </div>
          <div style={sx('display:grid;grid-template-columns:minmax(0,132px) minmax(0,1.4fr) minmax(0,1fr);gap:13px;padding:8px 12px 6px;border-bottom:1px solid rgba(34,39,46,.1);')}>
            {['Detector', 'Actor · what fired', 'Verdict'].map((c) => (
              <span key={c} style={sx(HEAD)}>
                <span>{c}</span>
              </span>
            ))}
          </div>
          <div style={sx('min-width:0;')}>
            <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(290px,1fr));gap:0 16px;align-items:start;min-width:0;')}>
              {(ueba.data?.anomalies ?? []).map((u, n) => {
                const sc = sevOf(u.severity);
                return (
                  <div key={n} style={sx('display:grid;grid-template-columns:minmax(0,132px) minmax(0,1.4fr) minmax(0,1fr);gap:6px 13px;align-items:start;padding:10px 12px;border-bottom:1px solid rgba(34,39,46,.07);' + (u.severity === 'high' ? 'background:rgba(179,38,30,.035);' : ''))}>
                    <span style={sx('min-width:0;')}>
                      <span style={sx(chipS(sc[0], sc[1]))}>
                        <span>{u.type.toUpperCase()}</span>
                      </span>
                      <span style={sx('display:block;font-size:14px;color:#54606C;margin-top:4px;white-space:nowrap;')}>
                        <span>{u.pages === false ? 'surface only' : 'would page'}</span>
                      </span>
                    </span>
                    <span style={sx('min-width:0;')}>
                      <span style={sx('display:block;font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;overflow-wrap:anywhere;')}>
                        <span>{u.actorEmail ?? u.actorUserId}</span>
                      </span>
                      <span style={sx('display:block;font-size:14px;color:#54606C;margin-top:2px;')}>
                        <span>{u.actorRole ? (ROLE_LABEL as Record<string, string>)[u.actorRole] ?? (/^[A-Z_]+$/.test(u.actorRole) ? u.actorRole.replace(/_/g, ' ').toLowerCase() : u.actorRole) : ''}</span>
                      </span>
                      <span style={sx('display:block;font-size:14px;color:#3C454F;line-height:1.45;margin-top:4px;text-wrap:pretty;')}>
                        <span>{u.detail}</span>
                      </span>
                    </span>
                    <span style={sx('min-width:0;')}>
                      <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;line-height:1.4;text-wrap:pretty;color:' + sc[0] + ';')}>
                        <span>{u.verdict ?? u.type}</span>
                      </span>
                      <span style={sx('display:block;font-size:14px;letter-spacing:.08em;color:#54606C;margin-top:4px;')}>
                        <span>{u.severity === 'medium' ? 'MED' : u.severity.toUpperCase()}</span>
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          <div style={sx(NOTE)}>
            <span>Six detectors, each against the actor{'’'}s own recent baseline or their same-role peers. This view shows the actor, the counts and the verdict · never which resident was read. Reviewing a case is a separate, audited action.</span>
          </div>
          <div style={sx('font-size:14px;color:#54606C;margin-top:4px;line-height:1.5;max-width:100ch;text-wrap:pretty;')}>
            <span>Baselines need about two weeks per actor and the accumulated depth is not exposed, so volume and peer-outlier read as provisional and nothing here pages yet.</span>
          </div>
          <div style={sx('font-size:14px;color:#54606C;margin-top:4px;line-height:1.5;max-width:100ch;text-wrap:pretty;')}>
            <span>Volume is the actor{'’'}s own mean + 3{'σ'}. Peer-outlier is a leave-one-out ratio at or above 3{'×'} same-role peers, not a z-score, so one outlier cannot inflate the group it is measured against. Scope-novelty only fires once an actor has an established prior set, so a new hire does not. Someone with no history yet reads as a new account, never as dormant.</span>
          </div>
        </div>

        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:14px 16px;margin-top:12px;')}>
          <div style={sx(BLOCK_HEAD)}>
            <span style={sx(H14)}>Not armed yet</span>
          </div>
          <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:0 18px;align-items:start;min-width:0;')}>
            {(arm.data ?? []).map((a) => (
              <div key={a.key} style={sx('display:grid;grid-template-columns:minmax(0,210px) 96px minmax(0,1fr);gap:6px 13px;align-items:baseline;padding:10px 0;border-bottom:1px solid rgba(34,39,46,.07);')}>
                <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;min-width:0;overflow-wrap:anywhere;')}>
                  <span>{a.key}</span>
                </span>
                <span style={sx(chipS('#54606C', 'rgba(34,39,46,.07)'))}>
                  <span>{a.state.toUpperCase()}</span>
                </span>
                <span style={sx('font-size:14px;color:#54606C;line-height:1.5;min-width:0;text-wrap:pretty;')}>
                  <span>{a.why}</span>
                </span>
              </div>
            ))}
            {arm.error && <div style={sx('font-size:14px;color:#54606C;padding:10px 0;')}>The activation list is not available yet.</div>}
          </div>
        </div>
      </div>
    </>
  );
}

// ── Projected (design puIsFore, T7794-7824) ──

export const fmtMins = (min: number) => (min < 60 ? 'about ' + min + (min === 1 ? ' minute' : ' minutes') : 'about ' + Math.round(min / 60) + (Math.round(min / 60) === 1 ? ' hour' : ' hours'));
/** A canary's run streak in its own colour: a failing streak red, a passing one green, nothing run grey. */
export function streakTone(status: string, streak: number | null | undefined): string {
  if (!streak) return '#54606C';
  return status === 'fail' ? '#B3261E' : status === 'pass' ? '#1F6B52' : '#54606C';
}

export function soonestOf(rows: PulseProjection[]): number | null {
  const armed = rows.filter((r) => r.projected && r.minutes !== null);
  return armed.length ? Math.min(...armed.map((r) => r.minutes as number)) : null;
}

const WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];
const numWord = (n: number) => WORDS[n] ?? String(n);

export function ProjectedTab({ rows, missing, openIncident }: { rows: PulseProjection[]; missing: boolean; openIncident: (id: string) => void }) {
  const plain = useCustomerText();
  const soonest = soonestOf(rows);
  const OCHRE = '#5B6B7C';
  const on = rows.filter((r) => r.projected).length;
  const off = rows.length - on;
  return (
    <div data-screen-label="Projected" style={sx('border-top:1px solid rgba(34,39,46,.14);padding-top:12px;min-width:0;')} data-testid="pulse-projected">
      <div style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:4px;min-width:0;')}>
        <span style={sx('font-size:17px;font-weight:800;letter-spacing:-.016em;text-wrap:pretty;')}>
          <span>{missing ? plain('Projections are not served yet') : soonest === null ? 'No SLO is projected to breach' : 'Next SLO breach projected in ' + fmtMins(soonest)}</span>
        </span>
      </div>
      <div style={sx('font-size:14px;color:#54606C;margin-top:4px;line-height:1.5;max-width:94ch;text-wrap:pretty;')}>
        <span>{missing ? plain('GET /api/pulse/projections is not on the back end yet, so nothing is projected here.') : numWord(on) + (on === 1 ? ' projection runs' : ' projections run') + ' off rates the monitor already records. ' + numWord(off) + ' more cannot run because their probe is not armed: an unarmed probe forecasts nothing rather than guessing.'}</span>
      </div>
      <div style={sx('margin-top:8px;min-width:0;')}>
        <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(320px,1fr));gap:0 18px;align-items:start;min-width:0;')}>
          {rows.map((f) => {
            const t = f.projected && f.minutes !== null ? (f.minutes <= 540 ? '#B3261E' : OCHRE) : '#54606C';
            const pct = f.minutes === null ? 0 : Math.max(4, Math.min(100, Math.round((1 - f.minutes / 1440) * 100)));
            return (
              <div key={f.what} style={sx('padding:10px 0;border-top:1px solid rgba(34,39,46,.08);min-width:0;')}>
                <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:4px;min-width:0;')}>
                  <span style={sx('font-size:16px;font-weight:700;min-width:0;text-wrap:pretty;')}>
                    <span>{f.what}</span>
                  </span>
                  <BuildTagged tag={f.basis}><span style={sx('display:inline-flex;align-items:center;height:22px;padding:0 6px;border-radius:4px;font-size:14px;font-weight:700;letter-spacing:.05em;white-space:nowrap;' + (f.basis === 'MEASURED' ? 'color:#1F6B52;background:rgba(47,138,111,.12);' : 'color:#54606C;background:rgba(34,39,46,.07);'))}>
                    <span>{f.basis}</span>
                  </span></BuildTagged>
                  <span style={sx('display:inline-flex;align-items:center;height:22px;padding:0 6px;border-radius:4px;font-size:14px;font-weight:700;letter-spacing:.05em;white-space:nowrap;' + (f.projected ? 'color:#5B6B7C;background:rgba(91,107,124,.13);' : 'color:#54606C;background:rgba(34,39,46,.07);'))}>
                    <span>{f.projected ? 'PROJECTED' : 'NO PROJECTION'}</span>
                  </span>
                </div>
                <div style={sx('font-size:14px;color:#54606C;margin-top:3px;line-height:1.45;text-wrap:pretty;')}>
                  <span>{f.rate}</span>
                </div>
                <div style={sx('font-size:16px;font-weight:700;line-height:1.45;margin-top:4px;text-wrap:pretty;color:' + t + ';')}>
                  <span>{f.projection}</span>
                </div>
                <span style={sx('display:block;height:5px;border-radius:3px;background:rgba(34,39,46,.08);overflow:hidden;margin-top:7px;max-width:300px;')}>
                  <i style={sx('display:block;height:100%;border-radius:2px;transform-origin:left;width:' + pct + '%;background:' + t + ';')} />
                </span>
                <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:3px;margin-top:7px;min-width:0;')}>
                  <span style={sx('font-size:14px;letter-spacing:.1em;text-transform:uppercase;font-weight:700;color:#54606C;white-space:nowrap;')}>Do now</span>
                  <span style={sx('flex:1 1 170px;min-width:0;font-size:14px;font-weight:700;color:#22272E;line-height:1.45;text-wrap:pretty;')}>
                    <span>{f.action}</span>{' '}
                    <span style={sx('font-weight:500;color:#54606C;')}>
                      · <span>{f.owner}</span>
                    </span>
                  </span>
                  {f.incident_id && (
                    <a href="#" onClick={(e) => { pre(e); openIncident(f.incident_id as string); }} style={sx('display:inline-flex;align-items:center;height:24px;padding:0 10px;border-radius:6px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;background:rgba(34,39,46,.05);color:#2F5C97;border:1px solid rgba(34,39,46,.14);')}>
                      <span>{'Open ' + (f.incident_ref ?? '')}</span>
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div style={sx('font-size:14px;color:#54606C;margin-top:11px;padding-top:9px;border-top:1px solid rgba(34,39,46,.12);line-height:1.5;max-width:98ch;text-wrap:pretty;')}>
        <span>A projection is a straight-line reading of the current rate, not a prediction that it holds. Every number is read from the incident record it projects, so the two cannot drift. No uptime figure, no MTTR, and nothing computed from a probe that is off.</span>
      </div>
    </div>
  );
}
